var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import _ from 'lodash';
import DragAndDropHelper from 'mixins/DragAndDropHelper';
import ActivityStage from 'components/activity/ActivityStage.vue';
import ActivityTable from '../components/activity/ActivityTable.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import CorrectIcon from 'components/icons/WhiteCheckmark.vue';
import DynamicContent from '../components/activity/DynamicContent.vue';
import HtmlRenderer from 'components/activity/HtmlRenderer.vue';
import NextActivityButton from 'components/activity/NextActivityButton.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';
import WrongIcon from 'components/icons/WhiteX.vue';
import ActivityWrapper from '../components/activity/ActivityWrapper.vue';
import mixins from 'vue-typed-mixins';
const DRAG_DISTANCE_MIN = 200;
export default mixins(DragAndDropHelper()).extend({
    name: 'DragAndDropTable',
    components: {
        ActivityStage,
        ActivityTable,
        BlinkingCursorText,
        CorrectIcon,
        DynamicContent,
        HtmlRenderer,
        NextActivityButton,
        TextDisplayList,
        WrongIcon,
        ActivityWrapper
    },
    computed: {
        numOfAnswers() {
            var _a, _b;
            return ((_b = (_a = this.currentRound.table) === null || _a === void 0 ? void 0 : _a.rows.filter(row => row.values.find(v => v.correct)).length) !== null && _b !== void 0 ? _b : 0);
        },
        hasGuessedAllAnswers() {
            return this.correctGuesses.length === this.numOfAnswers;
        },
        dragTileMargin() {
            return this.currentRound.draggableItems.length <= 4 ? '20px' : '10px';
        }
    },
    mounted() {
        window.addEventListener('resize', this.recalibrateAnswersOnResize);
    },
    destroyed() {
        window.removeEventListener('resize', this.recalibrateAnswersOnResize);
    },
    methods: {
        // start activity helper methods
        getKeyboardConfig() {
            return this.getKeyboardConfigHelper();
        },
        loadActivity() {
            return __awaiter(this, void 0, void 0, function* () {
                this.loadActivityHelper();
            });
        },
        loadAudios(activityAudios) {
            return __awaiter(this, void 0, void 0, function* () {
                return this.loadAudiosHelper(activityAudios);
            });
        },
        startActivity() {
            this.setRoundHelper();
            this.$nextTick(() => {
                this.setActivityContainerHeight();
            });
        },
        // end activity helper methods
        // start activity specific methods
        findClosestDropArea(dragItemCoordinates) {
            const allDroppableAreas = this.getAllDroppableAreas();
            const { x: dragX, y: dragY } = dragItemCoordinates;
            const formattedDropAreas = allDroppableAreas.map((droppableArea, index) => {
                const { x: dropX, y: dropY } = droppableArea.position;
                const xDiff = Math.abs(dragX - dropX);
                const yDiff = Math.abs(dragY - dropY);
                const totalDiff = xDiff + yDiff;
                return {
                    droppableArea,
                    dropX,
                    dropY,
                    totalDiff
                };
            });
            const closestDropZone = formattedDropAreas.reduce((acc, curr) => {
                return acc.totalDiff < curr.totalDiff ? acc : curr;
            });
            return closestDropZone.totalDiff < DRAG_DISTANCE_MIN ? closestDropZone.droppableArea : null;
        },
        getDragContentBackground(item) {
            if (this.correctGuesses.some(guess => guess.draggableItem === item))
                return 'transparent';
            return this.defaultColor;
        },
        getDropZonePosition(closestDropZone, draggableId) {
            const originalDraggablePos = document
                .getElementById(`startingPoint-${draggableId}`)
                .getBoundingClientRect();
            const { position: closestDropZonePos } = closestDropZone;
            const xPos = closestDropZonePos.x -
                originalDraggablePos.x -
                (originalDraggablePos.width - closestDropZonePos.width) / 2;
            const yPos = closestDropZonePos.y -
                originalDraggablePos.y -
                (originalDraggablePos.height - closestDropZonePos.height) / 2;
            return { xPos: xPos + 1, yPos };
        },
        getDraggableItemCorrectlyGuessed(draggableItem) {
            return this.correctGuesses.some(guess => guess.draggableItem.id === draggableItem.id);
        },
        getIsCorrect(guessedAnswer) {
            return guessedAnswer.draggableItem.label === guessedAnswer.dropAreaId;
        },
        handleDrop(context, dropZone = null) {
            const { target } = context;
            const draggableId = target.id.split('-')[1];
            const draggableItem = this.getAssociatedDraggableItem(draggableId);
            if (!draggableItem)
                return;
            this.removeFromGuessed(draggableItem);
            const dragItemCoordinates = target.getBoundingClientRect();
            let closestDropZone = this.findClosestDropArea(dragItemCoordinates);
            if (dropZone) {
                closestDropZone = {
                    id: dropZone.id,
                    position: dropZone.getBoundingClientRect()
                };
            }
            if (closestDropZone) {
                const closestDropZoneId = closestDropZone.id.split('-')[1];
                const alreadyGuessed = [...this.guessed, ...this.correctGuesses].some(item => item.dropAreaId === closestDropZoneId);
                if (alreadyGuessed) {
                    this.resetDraggable(target);
                    return;
                }
                this.handleMoveToDropZone(target, closestDropZone, draggableItem, draggableId);
                return;
            }
            this.resetDraggable(target);
            this.removeFromGuessed(draggableItem);
        },
        handleMoveToDropZone(target, closestDropZone, draggableItem, draggableId) {
            const { id: dropZoneElId } = closestDropZone;
            const dropAreaId = dropZoneElId.toString().split('-')[1];
            const guessedArr = [...this.guessed].filter(guessed => guessed.draggableItem !== draggableItem);
            this.guessed = _.uniq([...guessedArr, { draggableItem, dropAreaId }]);
            const { xPos, yPos } = this.getDropZonePosition(closestDropZone, draggableId);
            this.moveDraggableToDropZone(target, xPos, yPos);
            this.selectedElement = null;
        },
        isElementSelected(item) {
            if (this.selectedElement) {
                return this.selectedElement.id === `draggable-${item.id}`;
            }
        },
        isGuessed(draggableItem) {
            if (!draggableItem)
                return false;
            return this.guessed.some(item => item.draggableItem.id === draggableItem.id);
        },
        isGuessedCorrectly(rowValue) {
            if (!rowValue)
                return false;
            return this.correctGuesses.some(item => item.dropAreaId === rowValue.value);
        },
        isGuessedIncorrectly(rowValue) {
            if (!rowValue)
                return false;
            const isInWrongGuess = this.wrongGuesses.some(item => item.dropAreaId === rowValue.value);
            const isGuessed = this.guessed.some(item => item.dropAreaId === rowValue.value);
            const isEmpty = !isGuessed && !this.isGuessedCorrectly(rowValue) && this.isSubmitted;
            return isInWrongGuess || isEmpty;
        },
        // start accessibility methods
        getDropItemAriaLabel(value, row) {
            var _a;
            const inputLabel = (_a = row.values.find(r => r.value !== value.value)) === null || _a === void 0 ? void 0 : _a.value;
            const guessed = [...this.guessed, ...this.correctGuesses].find(g => g.dropAreaId === value.value);
            const isCorrectlyGuessed = this.isGuessedCorrectly(value);
            if (guessed) {
                return `Dropzone for ${inputLabel} with ${isCorrectlyGuessed ? 'correct guess' : 'guess'} ${guessed.draggableItem.label}`;
            }
            return `Dropzone for ${inputLabel}`;
        },
        recalibrateAnswersOnResize() {
            if (!this.guessed.length)
                return;
            this.guessed.forEach(({ draggableItem, dropAreaId }) => {
                var _a;
                const target = document.getElementById(`draggable-${draggableItem.id}`);
                const closestDropZone = (_a = this.getAllDroppableAreas()) === null || _a === void 0 ? void 0 : _a.find(({ id }) => id === `dropArea-${dropAreaId}`);
                const { xPos, yPos } = this.getDropZonePosition(closestDropZone, draggableItem.id);
                this.moveDraggableToDropZone(target, xPos, yPos);
            });
        }
        // end accessibility methods
    },
    watch: {
        showCorrectAnswers(newVal) {
            var _a, _b;
            if (newVal) {
                const correctGuesses = [];
                (((_b = (_a = this.currentRound) === null || _a === void 0 ? void 0 : _a.table) === null || _b === void 0 ? void 0 : _b.rows) || []).forEach((row, rowIndex) => {
                    row.values.forEach((value, colIndex) => {
                        if (value.correct) {
                            correctGuesses.push(`The correct input for row ${rowIndex + 1} column ${colIndex + 1} is ${value.value}.`);
                        }
                    });
                });
                this.setScreenReaderAlert(correctGuesses.join(' '));
            }
        }
    }
});
